///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type EarthTile } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTile"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { cFragmentTextureUnits, tileFragmentTextureUnits } from "@/earthbrowser/utils/TextureUnitBudget"
import { Renderer } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import { clamp } from "@stovokor/math/math"
import { type matrix } from "@stovokor/math/matrix"
import { type vec2, type vec3 } from "@stovokor/math/vector"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { combineDefines, ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

const c_cloud_fade_range					= 3000.0
const c_cloud_min_vis_elevation				= 1500.0 + wgs.a
// The wave phases in earthtile.glsl are periodic in this many seconds
const c_water_time_period					= 3600.0

export class EarthTileRenderer extends Renderer {
	private tile: EarthTile

	private modelViewProjection = new UniformDynamic<matrix>()
	private altitudeScale = new UniformDynamic<number>()
	private lightObjectNormalized = new UniformDynamic<vec3>()
	private hasElevation = new UniformDynamic<boolean>()
	private parentElevation = new UniformDynamic<Texture>()
	private parentNormal = new UniformDynamic<Texture>()
	private parentTileTexture = new UniformDynamic<Texture>()
	private parentElevationDelta = new UniformDynamic<vec2>()
	private parentElevationFade = new UniformDynamic<number>()
	private textureFade = new UniformDynamic<number>()
	private tileFadeIn = new UniformDynamic<number>()
	private elevationMultiplier = new UniformDynamic<number>()
	private terrainSunLight = new UniformDynamic<number>()
	// Sun glint on water (see earthtile.glsl waterCoverage / sunGlint)
	private waterMask = new UniformDynamic<Texture>()
	private hasWaterMask = new UniformDynamic<boolean>()
	private cameraPosition = new UniformDynamic<vec3>()
	private waterTime = new UniformDynamic<number>()

	private ebContext: EarthBrowserContext

	constructor(context: StovokorContext, tile: EarthTile, materials: Material[]) {
		const defines = combineDefines(materials.map(o => o.shaderDefines).filter(n => n !== undefined))
		// When the overlays would push the fragment stage past its texture
		// units, sample the water mask in the vertex stage instead (see
		// TextureUnitBudget); the program links at the cost of a slightly
		// softer coastline on the glint
		if (tileFragmentTextureUnits(defines) > cFragmentTextureUnits) {
			defines.WATER_MASK_VERTEX = "1"
		}
		const source = new ShaderSourceDefinition("earthtile", defines)
		super(context, source, tile.geometry, [tile.material, ...materials])
		this.tile = tile
		this.ebContext = context as EarthBrowserContext
	}

	protected stateUpdate(state: RenderState): void {
		this.materials.forEach(o => o.renderUpdate?.(state))

		this.modelViewProjection.value = state.modelViewProjection
		this.lightObjectNormalized.value = RenderState.lightDirection
		this.elevationMultiplier.value = this.ebContext.terrainExaggeration
		this.terrainSunLight.value = this.ebContext.terrainSunLight ? 1 : 0

		const elevation = state.camera.position.length
		this.altitudeScale.value = clamp((elevation - c_cloud_min_vis_elevation)/c_cloud_fade_range, 0.0, 1.0)

		this.waterMask.value = this.ebContext.waterMask
		this.hasWaterMask.value = this.ebContext.waterMask.available
		this.cameraPosition.value = state.camera.position
		// Wall-clock seconds, wrapped so the shader's float keeps its precision
		this.waterTime.value = (state.realTime.julian * 86400) % c_water_time_period

		// Only tell shader we have elevation when it's actually loaded and ready to use
		this.hasElevation.value = this.tile.material.hasElevationData && this.tile.material.elevationReady
		this.parentElevation.value = this.tile.material.parentElevation
		this.parentNormal.value = this.tile.material.parentNormal
		this.parentTileTexture.value = this.tile.material.parentTileTexture
		this.parentElevationDelta.value = this.tile.material.parentElevationDelta
		this.parentElevationFade.value = this.tile.material.parentElevationFade
		this.textureFade.value = this.tile.material.textureFade
		this.tileFadeIn.value = this.tile.material.tileFadeIn

		this.renderAction = () => {
			if(this.tile.sectionsToRender === 0b1111) {
				this.tile.geometry.indexBuffer.draw(DrawMode.Triangles)
			} else {
				for(let i=0; i < this.tile.geometry.indexBuffer.groups.length; i++) {
					if(this.tile.sectionsToRender & (1 << i)) {
						this.tile.geometry.indexBuffer.drawGroup(i, DrawMode.Triangles)
					}
				}
			}
		}
	}
}